export type UsageCreditsStatus = 'normal' | 'low' | 'critical' | 'exhausted' | 'using_pack' | 'unavailable';
export type UsageCreditsAgent = 'CONSULTANT' | 'AUDITOR';

export interface AgentUsageCredits {
  agent: UsageCreditsAgent;
  quotaPool?: UsageCreditsAgent | 'FREE';
  spentCredits: number;
  remainingCredits: number | null;
  totalCredits: number | null;
  nextResetAt: string | null;
}

export interface UsageCredits {
  agents: AgentUsageCredits[];
  purchasedCredits: number;
}

export interface AgentUsageCreditsViewModel extends AgentUsageCredits {
  remainingPercentage: number | null;
  status: UsageCreditsStatus;
}

export interface UsageCreditsViewModel {
  agents: AgentUsageCreditsViewModel[];
  purchasedCredits: number;
}

function cleanCreditValue(value: number | null | undefined): number | null {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return null;
  }

  return Math.max(0, Math.floor(value));
}

function clampPercentage(value: number): number {
  return Math.max(0, Math.min(100, value));
}

function remainingCreditPercentage(remainingCredits: number | null, totalCredits: number | null): number | null {
  if (totalCredits === null) return null;
  if (totalCredits <= 0 || remainingCredits === null) return 0;
  return clampPercentage((remainingCredits / totalCredits) * 100);
}

export function resolveUsageCreditsStatus(
  remainingCredits: number | null,
  totalCredits: number | null,
  purchasedCredits = 0,
): UsageCreditsStatus {
  if (totalCredits === null) return 'unavailable';
  if (remainingCredits === null) return 'exhausted';
  if (remainingCredits <= 0) return purchasedCredits > 0 ? 'using_pack' : 'exhausted';
  if (totalCredits <= 0) return 'exhausted';

  const percentage = clampPercentage((remainingCredits / totalCredits) * 100);
  if (percentage > 30) return 'normal';
  if (percentage >= 10) return 'low';
  if (percentage >= 1) return 'critical';
  return 'exhausted';
}

export function buildUsageCreditsViewModel(usage: UsageCredits): UsageCreditsViewModel {
  const purchasedCredits = cleanCreditValue(usage.purchasedCredits) ?? 0;
  const agents = usage.agents.map((agent) => {
    const totalCredits = cleanCreditValue(agent.totalCredits);
    const remainingCredits = cleanCreditValue(agent.remainingCredits);
    const remainingPercentage = remainingCreditPercentage(remainingCredits, totalCredits);

    return {
      ...agent,
      spentCredits: cleanCreditValue(agent.spentCredits) ?? 0,
      totalCredits,
      remainingCredits,
      remainingPercentage,
      status: resolveUsageCreditsStatus(remainingCredits, totalCredits, purchasedCredits),
    };
  });

  return { agents, purchasedCredits };
}

export function formatCreditCount(value: number, locale: string): string {
  const normalizedValue = Math.max(0, Math.floor(value));
  const formattedValue = new Intl.NumberFormat(locale, {
    maximumFractionDigits: 0,
  }).format(normalizedValue);
  if (locale.toLocaleLowerCase().startsWith('fr')) {
    return `${formattedValue} ${normalizedValue === 1 ? 'crédit' : 'crédits'}`;
  }

  return `${formattedValue} ${normalizedValue === 1 ? 'credit' : 'credits'}`;
}

export function formatCreditRatio(remaining: number, total: number, locale: string): string {
  const remainingLabel = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 })
    .format(Math.max(0, Math.floor(remaining)));
  return `${remainingLabel} / ${formatCreditCount(total, locale)}`;
}

export function formatUsageCreditsDate(value: string | null, locale: string, compact = false): string | null {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: compact ? 'short' : 'long',
    year: compact ? undefined : 'numeric',
  }).format(date);
}
